import { create } from 'zustand'
import type { Region, ROICalculations } from '@/types/roi'

interface ROIState {
  // Regions
  regions: Region[]
  activeRegionId: string | null

  // View state
  isCustomerView: boolean
  expandedRegions: Set<string>

  // Calculations
  calculations: ROICalculations | null

  // Actions
  addRegion: (region: Omit<Region, 'id'>) => void
  updateRegion: (id: string, updates: Partial<Region>) => void
  removeRegion: (id: string) => void
  setActiveRegion: (id: string | null) => void
  toggleRegionExpanded: (id: string) => void
  setCustomerView: (isCustomerView: boolean) => void
  setCalculations: (calculations: ROICalculations) => void
  resetROI: () => void
  loadROIState: (state: Partial<ROIState>) => void
}

const generateId = () => `region-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

const initialState = {
  regions: [],
  activeRegionId: null,
  isCustomerView: false,
  expandedRegions: new Set<string>(),
  calculations: null,
}

export const useROIStore = create<ROIState>((set) => ({
  ...initialState,

  addRegion: (region) => {
    const id = generateId()
    set((state) => ({
      regions: [
        ...state.regions,
        {
          ...region,
          id,
          products: region.products || [],
        },
      ],
      activeRegionId: id,
      expandedRegions: new Set([...state.expandedRegions, id]),
    }))
  },

  updateRegion: (id, updates) =>
    set((state) => ({
      regions: state.regions.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    })),

  removeRegion: (id) =>
    set((state) => {
      const newRegions = state.regions.filter((r) => r.id !== id)
      const newExpanded = new Set(state.expandedRegions)
      newExpanded.delete(id)

      return {
        regions: newRegions,
        activeRegionId: state.activeRegionId === id ? null : state.activeRegionId,
        expandedRegions: newExpanded,
      }
    }),

  setActiveRegion: (id) => set({ activeRegionId: id }),

  toggleRegionExpanded: (id) =>
    set((state) => {
      const newExpanded = new Set(state.expandedRegions)
      if (newExpanded.has(id)) {
        newExpanded.delete(id)
      } else {
        newExpanded.add(id)
      }
      return { expandedRegions: newExpanded }
    }),

  setCustomerView: (isCustomerView) => set({ isCustomerView }),

  setCalculations: (calculations) => set({ calculations }),

  resetROI: () => set({ ...initialState, expandedRegions: new Set() }),

  loadROIState: (state) =>
    set({
      ...state,
      expandedRegions: new Set(state.expandedRegions || []),
    }),
}))
